import { useState, useEffect, useRef } from "react";

const CSS_FILES = [
  "/api/static/wikipedia-site-styles.css",
  "/api/static/wikipedia-vector-styles.css",
  "/api/static/wikipedia-gallery-styles.css",
  "/api/static/toc.css",
];

const CSS_FILES_MOBILE = ["/api/static/wikipedia_mobile_no_body_classes.css"];

async function fetchCssFiles(isMobile: boolean): Promise<string> {
  const cssFiles = isMobile ? CSS_FILES_MOBILE : CSS_FILES;

  const responses = await Promise.all(
    cssFiles.map(async (url) => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          console.warn(`Failed to fetch ${url}: ${response.status}`);
          return "";
        }
        return response.text();
      } catch (err) {
        console.warn(`Error fetching ${url}:`, err);
        return "";
      }
    }),
  );

  const externalStyles = responses.join("\n");
  return externalStyles + "\n" + INLINE_STYLES;
}

// [data-mw-section-id="0"] maybe some stuff should only be that section
// we could also align everything center for mobiiile in the sections but not sure what the consequences are
//
// the trick here is that we convert the sections to flex box and set the order of figure and table to 1 so other stuff comes first like a p tag
//
// .ambox is just a message like this wikipedia page needs some work or something we dont really want that if we want that this should always be first otherwise it looks weird
const INLINE_STYLES = `
@media (max-width: 640px) {
  section {
    display: flex;
    flex-direction: column;
  }

  section[data-mw-section-id]:not([data-mw-section-id="0"]) {
    position: relative;
  }

  section[data-mw-section-id]:not([data-mw-section-id="0"]) > h2,
  section[data-mw-section-id]:not([data-mw-section-id="0"]) > h3,
  section[data-mw-section-id]:not([data-mw-section-id="0"]) > h4,
  section[data-mw-section-id]:not([data-mw-section-id="0"]) > h5,
  section[data-mw-section-id]:not([data-mw-section-id="0"]) > h6 {
    cursor: pointer;
    user-select: none;
    position: relative;
    padding-left: 1.75rem;
  }

  section[data-mw-section-id]:not([data-mw-section-id="0"]) > h2::before,
  section[data-mw-section-id]:not([data-mw-section-id="0"]) > h3::before,
  section[data-mw-section-id]:not([data-mw-section-id="0"]) > h4::before,
  section[data-mw-section-id]:not([data-mw-section-id="0"]) > h5::before,
  section[data-mw-section-id]:not([data-mw-section-id="0"]) > h6::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    width: 1.25rem;
    height: 1.25rem;
    transform: translateY(-35%) rotate(90deg);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='15 18 9 12 15 6'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    transition: transform 0.2s;
  }

  section[data-mw-section-id]:not([data-mw-section-id="0"]).expanded > h2::before,
  section[data-mw-section-id]:not([data-mw-section-id="0"]).expanded > h3::before,
  section[data-mw-section-id]:not([data-mw-section-id="0"]).expanded > h4::before,
  section[data-mw-section-id]:not([data-mw-section-id="0"]).expanded > h5::before,
  section[data-mw-section-id]:not([data-mw-section-id="0"]).expanded > h6::before {
    transform: translateY(-35%) rotate(-90deg);
  }

  section[data-mw-section-id]:not([data-mw-section-id="0"]):not(.expanded) > *:not(h2):not(h3):not(h4):not(h5):not(h6) {
    display: none;
  }

  // table {
  //   order: 1;
  //   overflow: auto;
  //   align-self: center;
  // }

  // figure {
  //   order: 1;
  //   align-self: center;
  // }

  .ambox {
    display: none;
  }
}

.shadow-content a[hx-vals] {
  color: #0645ad !important;
  cursor: pointer !important;
}

.shadow-content a:not([hx-vals]) {
  color: inherit !important;
  text-decoration: none !important;
  cursor: default !important;
}

.shadow-content a[hx-vals]:visited {
  color: #0B0080 !important;
}

.shadow-content a[hx-vals]:hover {
  text-decoration: underline !important;
}

/* Dark mode - applied when shadow host has 'dark-mode' class */
:host(.dark-mode) .shadow-content a[hx-vals] {
  color: #88a3e8 !important;
}

:host(.dark-mode) .shadow-content a[hx-vals]:visited {
  color: #a594e8 !important;
}

:host(.dark-mode) .shadow-content {
  background-color: #18181b;
  color: #fafafa;
}

:host(.dark-mode) .shadow-content #article_name {
  color: #fafafa;
}

.shadow-content #article_name {
  color: #18181b;
}

#article_body {
  margin: 20px;
}

#article_name {
  margin: 0 20px 20px 20px;
}

/* Wikipedia-style horizontal lists */
.hlist dl,
.hlist ol,
.hlist ul {
  margin: 0;
  padding: 0;
}

.hlist dl dl,
.hlist ol dl,
.hlist ul dl,
.hlist dl ol,
.hlist ol ol,
.hlist ul ol,
.hlist dl ul,
.hlist ol ul,
.hlist ul ul {
  display: inline;
}

.hlist dd,
.hlist dt,
.hlist li {
  margin: 0;
  display: inline;
}

.hlist dt::after {
  content: ':';
}

.hlist dd::after,
.hlist li::after {
  content: ' ·';
  font-weight: bold;
}

.hlist dd:last-child::after,
.hlist dt:last-child::after,
.hlist li:last-child::after {
  content: none;
}

.hlist dd dd:first-child::before,
.hlist dt dd:first-child::before,
.hlist li dd:first-child::before,
.hlist dd dt:first-child::before,
.hlist dt dt:first-child::before,
.hlist li dt:first-child::before,
.hlist dd li:first-child::before,
.hlist dt li:first-child::before,
.hlist li li:first-child::before {
  content: '(';
  font-weight: normal;
}

.hlist dd dd:last-child::after,
.hlist dt dd:last-child::after,
.hlist li dd:last-child::after,
.hlist dd dt:last-child::after,
.hlist dt dt:last-child::after,
.hlist li dt:last-child::after,
.hlist dd li:last-child::after,
.hlist dt li:last-child::after,
.hlist li li:last-child::after {
  content: ')';
  font-weight: normal;
}

.hlist dd ol > li:first-child::before,
.hlist dt ol > li:first-child::before,
.hlist li ol > li:first-child::before {
  content: '(' counter(list-item) ' ';
}

.hlist ol {
  counter-reset: list-item;
}

.hlist ol > li {
  counter-increment: list-item;
}

.hlist ol > li::before {
  content: counter(list-item) ' ';
}

/* Wikipedia Navbox CSS */
.navbox {
    box-sizing: border-box;
    border: 1px solid #a2a9b1;
    width: 100%;
    clear: both;
    font-size: 88%;
    text-align: center;
    padding: 1px;
    margin: 1em auto 0;
    border-collapse: collapse;
}

.navbox-inner,
.navbox-subgroup {
    width: 100%;
}

.navbox-group,
.navbox-title,
.navbox-abovebelow {
    padding: 0.2em 1em;
}

.navbox-title {
    font-size: 110%;
    font-weight: bold;
    text-align: center;
    background-color: #ccccff;
}

.navbox-abovebelow {
    background-color: #ccccff;
}

.navbox-group {
    background-color: #ddddff;
    font-weight: bold;
    white-space: nowrap;
}

.navbox-list {
    text-align: left;
}

.navbox-odd {
    background-color: transparent;
}

.navbox-even {
    background-color: #f7f7f7;
}

/* Separator between items */
.navbox-list div {
    padding: 0em 0.25em;
}

.navbox-list a {
    white-space: nowrap;
}

/* Collapsible behavior */
.client-js .navbox .collapsible .navbox-title {
    cursor: pointer;
}

.client-js .navbox .collapsed .navbox-title {
    padding-left: 1.8em;
}

.client-js .navbox .mw-collapsible-toggle {
    float: left;
    margin-left: -1.8em;
    width: 1.8em;
}

/* Navbar mini links in title */
.navbox-title .navbar {
    display: inline-block;
    vertical-align: middle;
    margin-right: 0.5em;
}

.navbox-title .navbar abbr {
    font-size: 90%;
    cursor: pointer;
    color: #000;
    background: none;
    border: none;
    padding: 0;
}

/* Dark mode adjustments for navbox */
:host(.dark-mode) .navbox-title,
:host(.dark-mode) .navbox-abovebelow {
    background-color: #0a0a5a;
}

:host(.dark-mode) .navbox-group {
    background-color: #1a1a6a;
}

:host(.dark-mode) .navbox-even {
    background-color: #1a1a1a;
}

:host(.dark-mode) .navbox-title .navbar abbr {
    color: #fff;
}

:host(.dark-mode) .navbox {
    border-color: #4a5568;
}

/* Print styles */
@media print {
    .navbox {
        display: none;
    }
}
`;

let globalStylesCache: string | null = null;
let globalFetchPromise: Promise<string> | null = null;
let lastIsMobile: boolean | null = null;

async function getStyles(isMobile: boolean): Promise<string> {
  if (globalStylesCache && lastIsMobile === isMobile) {
    return globalStylesCache;
  }

  if (globalFetchPromise && lastIsMobile === isMobile) {
    return globalFetchPromise;
  }

  lastIsMobile = isMobile;
  globalFetchPromise = fetchCssFiles(isMobile).then((styles) => {
    globalStylesCache = styles;
    return styles;
  });

  return globalFetchPromise;
}

export type UseShadowStylesReturn = {
  styles: string | null;
  isLoading: boolean;
  error: Error | null;
};

export function useShadowStyles(): UseShadowStylesReturn {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 640px)").matches
      : false,
  );
  const [styles, setStyles] = useState<string | null>(globalStylesCache);
  const [isLoading, setIsLoading] = useState(!globalStylesCache);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 640px)");
    const handleChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    getStyles(isMobile)
      .then((result) => {
        if (mountedRef.current) {
          setStyles(result);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (mountedRef.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setIsLoading(false);
        }
      });

    return () => {
      mountedRef.current = false;
    };
  }, [isMobile]);

  return { styles, isLoading, error };
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest;

  describe("getStyles", () => {
    it("returns a promise", async () => {
      const promise = getStyles(false);
      expect(promise).toBeInstanceOf(Promise);
    });
  });
}
